import { SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, message, Select, Typography } from 'antd';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import {
  getAllDataPackageLoad,
  getAllDataPaymentMethodLoad,
  getAllSubcriptionOrders,
} from 'src/api/register_and_cancel_and_load_package';
import TableContent from 'src/components/CTable';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { orderStatus } from 'src/shared/common/package_assigned';
import { IOrderStatus, IPackageAssigned } from 'src/shared/interface/package_assigned';
import { ISubcriptionDataOrderList } from 'src/shared/interface/register_and_cancel_and_load';
import {
  convertNumberByUnit,
  exportExcel,
  formatDateTime,
  formatMoney,
  parseQueryString,
} from 'src/utils';
import { usePermission } from 'src/hooks/usePermission';

function LoadDataListPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const { openTabs } = useAppSelector((state) => state.global);
  const [data, setData] = useState<PaginationResponse<ISubcriptionDataOrderList[]>>();
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [dataPackage, setDataPackage] = useState<IOrderStatus[]>([]);
  const [dataPayMethod, setDataPayMethod] = useState<IOrderStatus[]>([]);
  const [unit, setUnit] = useState<string>('GB');
  const [_, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();

  const { hasPermission, PERMISSIONS } = usePermission();

  const fetchListSubcriptionOrders = async () => {
    const params = parseQueryString(location.search);
    const res = await getAllSubcriptionOrders(params);
    if (res) {
      setData(res);
    }
  };

  useEffect(() => {
    getAllDataPackageLoad().then((res) => {
      if (res) {
        setDataPackage(
          [{ value: '', label: 'Tất cả' }].concat(
            res.map((item: any) => ({
              label: item?.mbfCode ?? '',
              value: item?.mbfCode ?? '',
            })),
          ),
        );
      }
    });
    getAllDataPaymentMethodLoad().then((res) => {
      res &&
        setDataPayMethod(
          [{ value: '', label: 'Tất cả' }].concat(
            res.map((item: any) => ({
              label: item === 'DataAccount' ? 'Tài khoản data' : item,
              value: item,
            })),
          ),
        );
    });
  }, []);

  useEffect(() => {
    if (location.search === '') {
      form.resetFields();
    }
    fetchListSubcriptionOrders();
    setSortColumn(undefined);
  }, [location.search]);

  function handleExport() {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_nap_data_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    exportExcel(
      process.env.REACT_APP_API_URL +
        '/api/SubscriberData/export-excel' +
        location.search +
        `${location.search === '' ? `?dataUnit=${unit}` : `&dataUnit=${unit}`}`,
    )
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error: { status?: number }) => {
        if (error.status === 403) {
          message.error('Tài khoản của bạn không có quyền truy cập vào chức năng này');
        }
      });
  }

  function handleFormSearch(value: any) {
    let values = { ...value };
    values = {
      ...values,
      id: values.id.trim() || undefined,
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.dateFrom ?? undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.dateTo ?? undefined,
      dateRange: undefined,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    setSearchParams({ pageIndex: 1, pageSize: 10, ...values });
  }

  function openCreateCancelPackagePage() {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'register-and-cancel-and-load-package/data-load/create',
      }),
    );
    if (
      !openTabs.find((tab) => tab.key === 'register-and-cancel-and-load-package/data-load/create')
    ) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Tạo mới đơn hàng Nạp data',
              children: '',
              key: 'register-and-cancel-and-load-package/data-load/create',
              closable: true,
            },
          ],
        }),
      );
    }
    dispatch(setGlobalState({ order: undefined }));
  }

  const handleDetailCancel = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'register-and-cancel-and-load-package/data-load/detail' + '?id=' + order.id,
      }),
    );
    if (
      !openTabs.find(
        (tab) =>
          tab.key === 'register-and-cancel-and-load-package/data-load/detail' + '?id=' + order.id,
      )
    ) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết Nạp data ' + order.id,
              children: '',
              key: 'register-and-cancel-and-load-package/data-load/detail' + '?id=' + order.id,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/register-and-cancel-and-load-package/data-load/detail?id=${order.id}`);
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn',
      fieldName: 'id',
      isHideSort: true,
      renderSpecialCard: (order: ISubcriptionDataOrderList) => (
        <button
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            hasPermission(PERMISSIONS.PACKAGE_MANAGEMENT.LOAD_DATA_VIEW_DETAIL)
              ? 'text-blue-400'
              : 'text-gray-400'
          } font-semibold`}
          onClick={() => handleDetailCancel(order)}
          disabled={!hasPermission(PERMISSIONS.PACKAGE_MANAGEMENT.LOAD_DATA_VIEW_DETAIL)}
        >
          {order.id ?? ''}
        </button>
      ),
    },
    {
      name: 'Mã gói',
      fieldName: 'mbfCode',
      isHideSort: true,
    },
    {
      name: 'Trạng thái',
      fieldName: 'statusString',
      renderSpecialCard: (record: ISubcriptionDataOrderList) => {
        const status = orderStatus.find((item) => item.value === record.statusString);
        return <span className='whitespace-nowrap'>{status?.label ?? ''}</span>;
      },
    },
    {
      name: 'Số giao dịch thanh toán',
      fieldName: 'transactionId',
      isHideSort: true,
    },
    {
      name: 'PTTT',
      fieldName: 'paymentMethod',
      isHideSort: true,
      renderSpecialCard: (data: ISubcriptionDataOrderList) => {
        if (data.paymentMethod === 'LocalPay') return 'Localpay';
        if (data.paymentMethod === 'DataAccount') return 'Tài khoản data';
        return '';
      },
    },
    {
      name: 'Thành tiền',
      fieldName: 'toTalAmount',
      isHideSort: true,
      renderSpecialCard: (data: ISubcriptionDataOrderList) => (
        <span className='whitespace-nowrap'>{formatMoney(data.toTalAmount)}</span>
      ),
    },
    {
      name: 'Tổng dung lượng',
      fieldName: '',
      isHideSort: true,
      renderSpecialCard: (record: ISubcriptionDataOrderList) => {
        return (
          <span className='whitespace-nowrap'>
            {convertNumberByUnit(record.extraDataObject.totalData, 'KB', unit)}
          </span>
        );
      },
    },
    {
      name: 'Đơn vị tính',
      fieldName: '',
      isHideSort: true,
      renderSpecialCard: () => {
        return <span className='whitespace-nowrap'>{unit}</span>;
      },
    },
    {
      name: 'Tổng cộng',
      fieldName: 'countSerial',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thành công',
      fieldName: 'quantitySuccess',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thất bại',
      fieldName: 'quantityFailed',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Người tạo',
      fieldName: 'createdBy',
    },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.createdAt ? formatDateTime(record.createdAt) : ''}</span>;
      },
    },
    {
      name: 'Ngày cập nhật cuối',
      fieldName: 'updatedAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return (
          <span>
            {record.updatedAt &&
            record.createdAt &&
            new Date(record.updatedAt).getTime() === new Date(record.createdAt).getTime()
              ? ''
              : record.updatedAt
              ? formatDateTime(record.updatedAt)
              : ''}
          </span>
        );
      },
    },
    {
      name: 'Người cập nhật cuối',
      fieldName: 'updatedBy',
    },
  ];

  return (
    <Fragment>
      <Form form={form} onFinish={handleFormSearch}>
        <div className='md:flex md:gap-4 md:flex-wrap'>
          <Form.Item name='id' initialValue={''}>
            <Input prefix={<SearchOutlined />} placeholder='Mã đơn' allowClear size='middle' />
          </Form.Item>
          <Form.Item name='mbfCode' label='Gói'>
            <Select
              showSearch
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={dataPackage.map((item) => ({
                value: item.value,
                label: item.label,
              }))}
              size='middle'
              className='min-w-[200px]'
            />
          </Form.Item>
          <Form.Item name='paymentMethod' label='PTTT'>
            <Select
              allowClear
              showSearch
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={dataPayMethod}
              size='middle'
              className='min-w-[200px]'
            />
          </Form.Item>
          <Form.Item name='status' label='Trạng thái'>
            <Select
              showSearch
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={orderStatus}
              size='middle'
              className='min-w-[200px]'
            />
          </Form.Item>
          <Form.Item name='' label='Người tạo'>
            <Select
              showSearch
              defaultValue={merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
              size='middle'
              className='min-w-[200px]'
            >
              <Select.Option value='merchantName'>
                {merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name='dateRange' label='Ngày tạo'>
            <DatePicker.RangePicker
              disabledDate={(current) => current && current >= moment().endOf('day')}
              placeholder={['Từ', 'Đến']}
              format={'DD/MM/YYYY'}
              size='middle'
              style={{ width: '100%' }}
            />
          </Form.Item>

          <div className='flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end w-full'>
            <Button
              type='primary'
              htmlType='submit'
              size='middle'
              className='w-full sm:w-auto order-1 sm:order-none'
            >
              Tìm kiếm
            </Button>
            {hasPermission(PERMISSIONS.PACKAGE_MANAGEMENT.LOAD_DATA_CREATE_ORDER) && (
              <Button
                onClick={openCreateCancelPackagePage}
                size='middle'
                className='w-full sm:w-auto'
              >
                Tạo mới
              </Button>
            )}
            {hasPermission(PERMISSIONS.PACKAGE_MANAGEMENT.LOAD_DATA_EXPORT) && (
              <Button onClick={handleExport} size='middle' className='w-full sm:w-auto'>
                Export
              </Button>
            )}
          </div>
        </div>
      </Form>

      <div className='mt-4 overflow-hidden'>
        <div className='my-3 flex flex-wrap justify-end items-center gap-2'>
          <Typography.Text>Hiển thị đơn vị dung lượng theo</Typography.Text>
          <Select
            className='min-w-[90px]'
            onSelect={(e) => setUnit(e)}
            value={unit}
            defaultValue={'KB'}
            options={[
              {
                label: 'KB',
                value: 'KB',
              },
              {
                label: 'MB',
                value: 'MB',
              },
              {
                label: 'GB',
                value: 'GB',
              },
              {
                label: 'TB',
                value: 'TB',
              },
            ]}
          />
        </div>
        <TableContent
          bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Không tìm thấy kết quả' }}
          dataSource={data?.results ?? []}
          columns={columns}
          totalRecord={data?.rowCount ?? 0}
          sortColumn={sortColumn}
          size='middle'
          onPageChange={(pageNumber, pageSize) => {
            const pagination = parseQueryString(location.search);
            setSearchParams({
              ...pagination,
              pageIndex: pageNumber ?? 1,
              pageSize: pageSize ?? 10,
            });
          }}
          onSort={(sort: ISortColumn) => {
            const pagination = parseQueryString(location.search);
            handleFormSearch({
              ...pagination,
              pageIndex: pagination.pageIndex ?? 1,
              pageSize: pagination.pageSize ?? 10,
              sortField: sort.fieldName,
              asc:
                sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
            });
          }}
        />
      </div>
    </Fragment>
  );
}

export default LoadDataListPage;
